import { useRef, useCallback, useEffect } from 'preact/hooks';
import { Tracking } from '../utils/tracking.js';

export function useAutoScroll(configClient, sendRef) {
  const transcriptRef = useRef(null);
  const observerRef = useRef(null);
  const actionClickRef = useRef(null);

  const stickToBottomRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const lastScrollHeightRef = useRef(0);
  const shouldForceFinalScrollRef = useRef(false);
  const ignoreScrollEventsRef = useRef(false);

  const scrollToBottom = useCallback((opts = {}) => {
    const el = transcriptRef.current;
    if (!el) return;
    const behavior = opts.smooth ? 'smooth' : 'auto';
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const onScroll = useCallback(() => {
    const el = transcriptRef.current;
    if (!el) return;
    const st = el.scrollTop;
    const sh = el.scrollHeight;
    const atBottom = sh - st - el.clientHeight < 30;

    const didHeightChange = lastScrollHeightRef.current !== sh;

    if (!ignoreScrollEventsRef.current && !didHeightChange) {
      if (lastScrollTopRef.current - st > 2) {
        stickToBottomRef.current = false;
      } else if (atBottom) {
        stickToBottomRef.current = true;
      }
    }

    lastScrollTopRef.current = st;
    lastScrollHeightRef.current = sh;
  }, []);

  const setTranscriptRef = useCallback((el) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (transcriptRef.current) {
      transcriptRef.current.removeEventListener('scroll', onScroll);
      if (actionClickRef.current) {
        transcriptRef.current.removeEventListener('click', actionClickRef.current);
      }
    }

    transcriptRef.current = el;

    if (el) {
      lastScrollTopRef.current = el.scrollTop;
      lastScrollHeightRef.current = el.scrollHeight;
      el.addEventListener('scroll', onScroll, { passive: true });

      actionClickRef.current = (e) => {
        const btn = e.target.closest('[data-mta-action]');
        if (btn && sendRef.current) {
          e.preventDefault();
          sendRef.current(btn.dataset.mtaAction, 'followup');
          return;
        }
        const link = e.target.closest('a');
        if (link) {
          Tracking.trackResultClick(configClient, link.innerText || link.textContent || '', link.href);
        }
      };
      el.addEventListener('click', actionClickRef.current);

      const observer = new MutationObserver(() => {
        if (stickToBottomRef.current || shouldForceFinalScrollRef.current) {
          scrollToBottom();
          if (shouldForceFinalScrollRef.current) {
            shouldForceFinalScrollRef.current = false;
            ignoreScrollEventsRef.current = false;
            stickToBottomRef.current = true;
          }
        }
      });
      observer.observe(el, { childList: true, subtree: true, characterData: true });
      observerRef.current = observer;
    }
  }, [scrollToBottom, onScroll, configClient, sendRef]);

  useEffect(() => {
    const onResize = () => {
      if (stickToBottomRef.current) {
        scrollToBottom();
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [scrollToBottom]);

  return {
    transcriptRef,
    setTranscriptRef,
    scrollToBottom,
    stickToBottomRef,
    shouldForceFinalScrollRef,
    ignoreScrollEventsRef
  };
}
